import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SourceDocument } from "../../../../api/content/sourcesApi";
import { formatDocumentVersionRef } from "../../../../shared/references/contentReferences";
import type { EvidenceMark } from "../../../annotation/textEvidence";
import type { ChatReference } from "../../../chatReferences";
import { absorbNamedIndividuals, describeStatement, groupIndividuals, layOutStatement, mostSpecificClasses, nestSaid, saidFirst, type ShownStatement } from "../../../knowledgeData/abox/aboxViewModel";
import { useAboxViewProfile } from "../../../knowledgeData/abox/aboxViewProfile";
import { statementCard } from "../../../knowledgeData/abox/statementCard";
import { termName, useTermCardsByCommit } from "../../../ontology";
import DocumentParts, { type DrawnAnnotations } from "../parts/DocumentParts";
import PartStatements from "./PartStatements";
import { itemKeys, regestParts, type PlacedStatement, type RegestResult } from "./regestParts";

interface TextWithStatementsProps {
  readonly document: Pick<SourceDocument, "ref" | "fullText" | "segments" | "metadata">;
  /**
   * The project the statements belong to, whose view profile lays them out and whose model
   * versions name their terms (AB-5), whatever the project menu chooses meanwhile (Sol review R3);
   * `null` when it isn't known: then every row shows, its terms by their own names.
   */
  readonly project: string | null;
  /** The statements drawn, accepted or not (F154); evidence on other documents or versions is not drawn. */
  readonly results: readonly RegestResult[];
  /** The annotation set drawn on the text; `null` draws none. */
  readonly annotations: DrawnAnnotations | null;
  /** Where selecting text asks the agent about it (F96); `undefined` without a project. */
  readonly askInProject: string | undefined;
  /** What a selection in the text also sends, such as the annotation set on screen (PC-6). */
  readonly askWith: Omit<ChatReference, "quote"> | null;
  /** What a statement's line also shows, such as the parts of a vita it came from. */
  readonly statementExtra?: (statement: PlacedStatement) => ReactNode;
}

/**
 * A document's text with the statements about it (O-1), for every page that shows instance data:
 * the Documents page, a result's page and its review steps (F154: one view, before and after
 * acceptance). The text shows once, part by part; each statement sits beside the part that holds
 * most of its words, once (`absorbNamedIndividuals`); one with no words in this text follows it.
 * Pointing at a statement or its words highlights both; clicking keeps them highlighted and
 * brings the other side into view; Escape lets go.
 */
export default function TextWithStatements({ document, project, results, annotations, askInProject, askWith, statementExtra }: TextWithStatementsProps) {
  const { t, i18n } = useTranslation();
  const [pointed, setPointed] = useState<readonly string[] | null>(null);
  const [selected, setSelected] = useState<readonly string[] | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const shown = formatDocumentVersionRef(document.ref);
  const language = i18n.resolvedLanguage ?? "en";
  const individuals = useMemo(() => groupIndividuals(results.flatMap((result) => result.statements), language), [results, language]);
  const profile = useAboxViewProfile(project);
  const placement = useMemo(() => regestParts(shown, document.segments, results), [shown, document.segments, results]);
  const commits = useMemo(() => [...new Set([...placement.parts.values(), placement.unplaced].flat().map((placed) => placed.modelCommit))].filter((commit) => commit !== ""), [placement]);
  const cards = useTermCardsByCommit(project, commits);
  const { byPart, unplaced } = useMemo(() => {
    // Each statement is laid out once, by its project's profile and its model version's terms
    // (AB-5); until both are read, the text shows without them, so nothing jumps when they come.
    if (profile === null || commits.some((commit) => !cards.has(commit))) return { byPart: new Map<string, ShownStatement<PlacedStatement>[]>(), unplaced: [] };
    const layOut = (placed: PlacedStatement) => {
      const terms = cards.get(placed.modelCommit) ?? null;
      const descriptions = describeStatement(placed.statement, individuals, (iri) => terms?.has(iri) === true);
      return layOutStatement(descriptions, profile, (iri) => terms?.get(iri)?.parents ?? []);
    };
    return {
      byPart: new Map([...placement.parts].map(([segmentPath, statements]) => [segmentPath, saidFirst(absorbNamedIndividuals(statements, layOut))])),
      unplaced: saidFirst(absorbNamedIndividuals(placement.unplaced, layOut)),
    };
  }, [placement, commits, individuals, cards, profile]);
  // Every statement key leads to all the keys of the line that shows it.
  const lineKeys = useMemo(() => new Map([...byPart.values(), unplaced].flat().flatMap((item) => itemKeys(item).map((key) => [key, itemKeys(item)] as const))), [byPart, unplaced]);
  // Statements are numbered in reading order, as in Daniel's viewer. Where the profile shapes a card, only a line
  // that reads as one is a statement; one that only names an entity is not numbered (usability round 3).
  const lines = useMemo(() => [...byPart.values(), unplaced].flat().filter((item) => item.layout.said.length > 0
    && (profile?.statements == null || statementCard(nestSaid(item.layout.said)[0], profile.statements) !== null)), [byPart, unplaced, profile]);
  const numbers = useMemo(() => new Map(lines.map((item, index) => [item.shown.key, index + 1])), [lines]);
  const marks = useMemo((): EvidenceMark[] => {
    const drawn = results.flatMap((result) => result.statements.flatMap((statement) => statement.evidence
      .filter((span) => span.segment.split("#")[0] === shown)
      .map((span) => ({ key: `${result.source}#${String(statement.number)}`, start: span.start, end: span.end }))));
    // Each span of a numbered statement is drawn as the annotations' labelled bar, "#n kind" (Johannes, 4 Nov 06:51:
    // "the annotation view is a must with the spans, similar to the regest viewer"; 10:46: "I don'T see the spans").
    const labelOf = new Map<string, string>();
    lines.forEach((item, index) => {
      const terms = cards.get(item.shown.modelCommit) ?? null;
      const about = statementCard(nestSaid(item.layout.said)[0], profile?.statements ?? null)?.about.individual;
      const kinds = about === undefined ? [] : mostSpecificClasses(about.classes, (iri) => terms?.get(iri)?.parents ?? []).map((iri) => termName(iri, terms, language));
      const label = [`#${String(index + 1)}`, ...kinds].join(" ");
      for (const key of itemKeys(item)) if (!labelOf.has(key)) labelOf.set(key, label);
    });
    return drawn.map((mark) => (labelOf.has(mark.key) ? { ...mark, label: labelOf.get(mark.key) } : mark));
  }, [results, shown, lines, cards, profile, language]);

  const linesOf = (keys: readonly string[]) => [...new Set(keys.flatMap((key) => lineKeys.get(key) ?? [key]))];
  const focus = {
    highlighted: new Set(pointed ?? selected ?? []),
    selected: new Set(selected ?? []),
    onPoint: (keys: readonly string[] | null) => { setPointed(keys === null ? null : linesOf(keys)); },
    onSelect: (keys: readonly string[]) => {
      const lines = linesOf(keys);
      const again = selected !== null && lines.every((key) => selected.includes(key));
      setSelected(again ? null : lines);
      if (again) return;
      // The other side comes into view: the line for clicked words, the words for a clicked line.
      requestAnimationFrame(() => {
        container.current?.querySelector(`[data-statements~=${JSON.stringify(lines[0] ?? "")}]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        container.current?.querySelector(".app-evidence-highlighted")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
  };
  const statementsOf = (key: string, items: typeof unplaced) => <PartStatements key={key} items={items} cards={cards} focus={focus} askInProject={askInProject} extra={statementExtra} numbers={numbers} shape={profile?.statements ?? null} />;
  return (
    <div ref={container} onKeyDown={(event) => { if (event.key === "Escape") setSelected(null); }}>
      <DocumentParts
        document={document}
        annotations={annotations}
        evidence={marks.length === 0 ? null : { marks, ...focus }}
        asides={new Map([...byPart].map(([segmentPath, items]) => [segmentPath, statementsOf(segmentPath, items)]))}
        askInProject={askInProject}
        askWith={askWith}
      />
      {unplaced.length === 0 ? null : (
        <section className="space-y-1 py-3">
          <h4 className="app-label text-xs">{t("sources.document.regest.unplaced")}</h4>
          {statementsOf("unplaced", unplaced)}
        </section>
      )}
    </div>
  );
}
